/**
 * AdminIntegrationsPage — Third-party integration management.
 * Persists enabled state + webhook config per organization via org_integrations table.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Package, Webhook, Building2, Settings, X, Eye, EyeOff, Copy, CheckCheck } from 'lucide-react';
import { ModuleCard, ModuleLoadingState } from '../../shared/components/ModulePageHeader';
import { AdminConsoleService } from '../../../services/adminConsoleService';
import { supabase } from '../../../lib/supabaseClient';
import type { IntegrationConfig, OrgIntegration } from '../../../types/adminConsole';

const STATIC_INTEGRATIONS: Omit<IntegrationConfig, 'enabled' | 'status' | 'webhook_url' | 'webhook_secret' | 'events'>[] = [
  { id: 'slack',    key: 'slack',    label: 'Slack',            description: 'Receba notificações de workflow e tarefas no Slack.',         category: 'communication' },
  { id: 'teams',   key: 'teams',   label: 'Microsoft Teams',   description: 'Integração com canais do Microsoft Teams.',                   category: 'communication' },
  { id: 'powerbi', key: 'powerbi', label: 'Power BI',          description: 'Exportar dados de KPIs e métricas para dashboards Power BI.', category: 'analytics'    },
  { id: 'webhooks',key: 'webhooks',label: 'Webhooks',          description: 'Envie eventos para endpoints externos via HTTP.',              category: 'webhook'      },
  { id: 'sap',     key: 'sap',     label: 'SAP ERP',           description: 'Sincronização de processos com sistemas SAP.',                category: 'erp'          },
  { id: 'totvs',   key: 'totvs',   label: 'TOTVS',             description: 'Integração com ERP TOTVS Protheus.',                         category: 'erp'          },
];

const WEBHOOK_EVENTS = [
  { key: 'execution.created',   label: 'Execução criada'     },
  { key: 'execution.completed', label: 'Execução concluída'  },
  { key: 'execution.cancelled', label: 'Execução cancelada'  },
  { key: 'execution.overdue',   label: 'Execução em atraso'  },
  { key: 'task.completed',      label: 'Tarefa concluída'    },
  { key: 'task.assigned',       label: 'Tarefa atribuída'    },
];

const CATEGORY_LABELS: Record<string, string> = {
  communication: 'Comunicação',
  analytics: 'Analytics',
  erp: 'ERP / Sistemas',
  webhook: 'Webhooks',
};

function mergeWithDb(dbRows: OrgIntegration[]): IntegrationConfig[] {
  return STATIC_INTEGRATIONS.map((s) => {
    const row = dbRows.find((r) => r.integration_key === s.key);
    return {
      ...s,
      enabled: row?.enabled ?? false,
      status: (row?.enabled ? 'connected' : 'disconnected') as IntegrationConfig['status'],
      webhook_url: row?.webhook_url ?? null,
      webhook_secret: row?.webhook_secret ?? null,
      events: row?.events ?? null,
      configured_at: row?.configured_at ?? undefined,
    };
  });
}

// ─── Webhook Config Modal ─────────────────────────────────

interface WebhookModalProps {
  integration: IntegrationConfig;
  orgId: string;
  onSave: (patch: { webhook_url: string; webhook_secret: string; events: string[] }) => Promise<void>;
  onClose: () => void;
}

const WebhookModal: React.FC<WebhookModalProps> = ({ integration, orgId, onSave, onClose }) => {
  const [url, setUrl] = useState(integration.webhook_url ?? '');
  const [secret, setSecret] = useState(integration.webhook_secret ?? '');
  const [events, setEvents] = useState<string[]>(integration.events ?? []);
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const toggleEvent = (key: string) =>
    setEvents((prev) => prev.includes(key) ? prev.filter((e) => e !== key) : [...prev, key]);

  const generateSecret = () => {
    const arr = new Uint8Array(24);
    crypto.getRandomValues(arr);
    setSecret(Array.from(arr).map((b) => b.toString(16).padStart(2, '0')).join(''));
  };

  const copySecret = async () => {
    if (!secret) return;
    await navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSave = async () => {
    if (!url.trim()) return;
    setSaving(true);
    try {
      await onSave({ webhook_url: url.trim(), webhook_secret: secret.trim(), events });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="enterprise-card-surface w-full max-w-lg rounded shadow-xl border border-enterprise-border-soft mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-enterprise-border-soft">
          <div className="flex items-center gap-2.5">
            <Webhook size={16} className="text-enterprise-primary" />
            <span className="text-[13px] font-semibold enterprise-text-primary">Configurar Webhooks</span>
          </div>
          <button type="button" onClick={onClose} className="enterprise-text-secondary hover:enterprise-text-primary transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* URL */}
          <div>
            <label className="block text-[11px] font-bold enterprise-text-secondary mb-1.5 uppercase tracking-wider">Endpoint URL *</label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://seu-sistema.com/webhook"
              className="enterprise-input w-full text-[12px]"
            />
            <p className="text-[10px] enterprise-text-muted mt-1">O Workflow Manager enviará um POST com payload JSON assinado para este URL.</p>
          </div>

          {/* Secret */}
          <div>
            <label className="block text-[11px] font-bold enterprise-text-secondary mb-1.5 uppercase tracking-wider">Secret HMAC</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type={showSecret ? 'text' : 'password'}
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  placeholder="Chave para verificar assinatura"
                  className="enterprise-input w-full text-[12px] pr-8"
                />
                <button type="button" onClick={() => setShowSecret((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 enterprise-text-secondary">
                  {showSecret ? <EyeOff size={12} /> : <Eye size={12} />}
                </button>
              </div>
              <button type="button" onClick={generateSecret} className="enterprise-btn-secondary text-[11px] px-2.5 shrink-0">Gerar</button>
              <button type="button" onClick={copySecret} className="enterprise-btn-secondary text-[11px] px-2.5 shrink-0" title="Copiar secret">
                {copied ? <CheckCheck size={13} className="text-emerald-500" /> : <Copy size={13} />}
              </button>
            </div>
            <p className="text-[10px] enterprise-text-muted mt-1">Usado para validar o header <code className="font-mono">X-WF-Signature</code> nos seus endpoints.</p>
          </div>

          {/* Events */}
          <div>
            <label className="block text-[11px] font-bold enterprise-text-secondary mb-2 uppercase tracking-wider">Eventos subscritos</label>
            <div className="grid grid-cols-2 gap-1.5">
              {WEBHOOK_EVENTS.map((ev) => (
                <label key={ev.key} className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={events.includes(ev.key)}
                    onChange={() => toggleEvent(ev.key)}
                    className="rounded border-enterprise-border-soft accent-enterprise-primary"
                  />
                  <span className="text-[11px] enterprise-text-primary group-hover:text-enterprise-primary transition-colors">{ev.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-enterprise-border-soft">
          <button type="button" onClick={onClose} className="enterprise-btn-secondary text-[12px]">Cancelar</button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !url.trim()}
            className="enterprise-btn-primary text-[12px] disabled:opacity-50"
          >
            {saving ? 'Salvando...' : 'Salvar configuração'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────

export const AdminIntegrationsPage: React.FC<{ externalSelectedOrg?: string }> = ({ externalSelectedOrg }) => {
  const [orgs, setOrgs] = useState<{ id: string; name: string }[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [integrations, setIntegrations] = useState<IntegrationConfig[]>(mergeWithDb([]));
  const [loading, setLoading] = useState(false);
  const [webhookTarget, setWebhookTarget] = useState<IntegrationConfig | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    AdminConsoleService.getOrganizations().then((list) => {
      setOrgs(list);
      if (list.length > 0) setSelectedOrgId(list[0].id);
    });
  }, []);

  // Sync with external org selection from ribbon
  useEffect(() => {
    if (externalSelectedOrg !== undefined) setSelectedOrgId(externalSelectedOrg);
  }, [externalSelectedOrg]);

  const loadIntegrations = useCallback(async (orgId: string) => {
    setLoading(true);
    try {
      const rows = await AdminConsoleService.getOrgIntegrations(orgId);
      setIntegrations(mergeWithDb(rows));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedOrgId) loadIntegrations(selectedOrgId);
  }, [selectedOrgId, loadIntegrations]);

  const handleToggle = async (integration: IntegrationConfig) => {
    if (!selectedOrgId) return;
    const newEnabled = !integration.enabled;
    // Optimistic update
    setIntegrations((prev) => prev.map((i) =>
      i.key === integration.key
        ? { ...i, enabled: newEnabled, status: newEnabled ? 'connected' : 'disconnected' }
        : i
    ));
    setSaving(integration.key);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await AdminConsoleService.upsertOrgIntegration(
        selectedOrgId,
        integration.key,
        { enabled: newEnabled },
        user?.id ?? '',
      );
    } catch {
      // Revert on error
      setIntegrations((prev) => prev.map((i) =>
        i.key === integration.key
          ? { ...i, enabled: !newEnabled, status: !newEnabled ? 'connected' : 'disconnected' }
          : i
      ));
    } finally {
      setSaving(null);
    }
  };

  const handleWebhookSave = async (patch: { webhook_url: string; webhook_secret: string; events: string[] }) => {
    if (!selectedOrgId || !webhookTarget) return;
    const { data: { user } } = await supabase.auth.getUser();
    await AdminConsoleService.upsertOrgIntegration(
      selectedOrgId,
      webhookTarget.key,
      { enabled: true, ...patch },
      user?.id ?? '',
    );
    await loadIntegrations(selectedOrgId);
  };

  const categories = Array.from(new Set(STATIC_INTEGRATIONS.map((i) => i.category)));

  return (
    <div className="enterprise-page h-full overflow-y-auto">

      {loading && <ModuleLoadingState label="Carregando integrações" />}

          {!loading && selectedOrgId && categories.map((cat) => {
            const items = integrations.filter((i) => i.category === cat);
            return (
              <ModuleCard key={cat} title={CATEGORY_LABELS[cat] ?? cat}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {items.map((integration) => (
                    <div
                      key={integration.key}
                      className={`flex items-start justify-between gap-3 p-3 rounded border transition-colors ${
                        integration.enabled
                          ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50/30 dark:bg-emerald-900/10'
                          : 'border-enterprise-border-soft'
                      }`}
                    >
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="shrink-0 h-8 w-8 rounded border border-enterprise-border-soft flex items-center justify-center enterprise-card-surface">
                          {integration.key === 'webhooks' ? (
                            <Webhook size={14} className="enterprise-text-secondary" />
                          ) : (
                            <Package size={14} className="enterprise-text-secondary" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[12px] font-semibold enterprise-text-primary">{integration.label}</span>
                            <span className={`text-[10px] font-medium rounded px-1.5 py-0.5 ${integration.enabled ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-slate-100 text-slate-500 dark:bg-zinc-800 dark:text-zinc-400'}`}>
                              {integration.enabled ? 'Conectado' : 'Desconectado'}
                            </span>
                          </div>
                          <p className="text-[11px] enterprise-text-secondary mt-0.5 leading-tight">{integration.description}</p>
                          {integration.key === 'webhooks' && integration.enabled && integration.webhook_url && (
                            <p className="text-[10px] font-mono enterprise-text-muted mt-1 truncate max-w-[220px]">{integration.webhook_url}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {integration.key === 'webhooks' && (
                          <button
                            type="button"
                            title="Configurar webhook"
                            onClick={() => setWebhookTarget(integration)}
                            className="enterprise-text-secondary hover:text-enterprise-primary transition-colors"
                          >
                            <Settings size={13} />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleToggle(integration)}
                          disabled={saving === integration.key}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-60 ${
                            integration.enabled ? 'bg-enterprise-primary' : 'bg-enterprise-border-strong'
                          }`}
                          role="switch"
                          aria-checked={integration.enabled}
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${integration.enabled ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </ModuleCard>
            );
          })}

          {!loading && !selectedOrgId && (
            <div className="mt-8 text-center enterprise-text-secondary text-[13px]">
              Selecione um cliente para gerenciar as integrações.
            </div>
          )}

          <div className="enterprise-card-surface p-3 text-[11px] enterprise-text-secondary border-l-2 border-blue-400 mt-2">
            As integrações são configuráveis por organização. Para integrações customizadas, utilize a API de Webhooks com os eventos disponíveis.
          </div>

        {webhookTarget && (
          <WebhookModal
            integration={webhookTarget}
            orgId={selectedOrgId}
            onSave={handleWebhookSave}
            onClose={() => setWebhookTarget(null)}
          />
        )}
    </div>
  );
};
